import { forwardRef, JSX, useRef } from 'react';
import { ControllerRenderProps, FieldPath, FieldValues } from 'react-hook-form';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import { InputBaseComponentProps } from '@mui/material/InputBase';
import TextField from '@mui/material/TextField';
import { useNumericField } from 'shared/components/formFields/numeric-field/hooks';
import styles from 'shared/components/formFields/numeric-field/numeric-field.module.scss';

type Props<TFieldValues extends FieldValues = FieldValues, TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>> = {
  field: ControllerRenderProps<TFieldValues, TName>;
  fieldName: string;
  label?: string;
  min: number;
  max: number;
  step?: number;
  id?: string;
  className?: string;
  error: boolean | undefined;
  helperText: string | undefined;
  disabled?: boolean;
  suffix?: (value: number) => string;
  showSpinners?: boolean;
};

interface InlineSuffixInputProps extends InputBaseComponentProps {
  suffixText?: string;
}

const InlineSuffixInput = forwardRef<HTMLElement, InlineSuffixInputProps>(({ suffixText, style, value, onFocus, ...props }, ref) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const charCount = String(value ?? '').length;

  const setRef = (node: HTMLInputElement | null): void => {
    inputRef.current = node;
    if (typeof ref === 'function') ref(node);
    else if (ref) ref.current = node;
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>): void => {
    const len = e.target.value.length;
    e.target.setSelectionRange(len, len);
    onFocus?.(e);
  };

  return (
    <Box
      component="span"
      sx={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0, cursor: 'text' }}
      onClick={() => inputRef.current?.focus()}
    >
      <input
        {...props}
        ref={setRef}
        value={value}
        onFocus={handleFocus}
        style={{
          ...style,
          width: `${Math.max(1, charCount)}ch`,
        }}
      />
      {suffixText && value != null && value !== '' && (
        <Box
          component="span"
          sx={{
            ml: 0.5,
            color: 'text.secondary',
            fontSize: 'inherit',
            whiteSpace: 'nowrap',
            pointerEvents: 'none',
            userSelect: 'none',
          }}
        >
          {suffixText}
        </Box>
      )}
    </Box>
  );
});
InlineSuffixInput.displayName = 'InlineSuffixInput';

const NumericField = <TFieldValues extends FieldValues = FieldValues, TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>>({
  field,
  fieldName,
  label,
  min,
  max,
  step = 1,
  id,
  className = 'form-field',
  error,
  helperText,
  disabled,
  suffix,
  showSpinners = true,
}: Readonly<Props<TFieldValues, TName>>): JSX.Element => {
  const { localValue, handleChange, handleBlur, handleIncrement, handleDecrement } = useNumericField({
    field: field as unknown as ControllerRenderProps<FieldValues, string>,
    fieldName,
    min,
    max,
    step,
    disabled,
  });

  const suffixText = suffix && localValue != null && localValue !== '' ? suffix(Number(localValue)) : undefined;

  const endAdornment = (): React.JSX.Element => (
    <InputAdornment position="end">
      <div className={styles.numericFieldArrowContainer}>
        <IconButton onClick={handleIncrement} size="small" sx={{ padding: '2px' }}>
          <KeyboardArrowUpIcon sx={{ fontSize: '16px' }} />
        </IconButton>
        <IconButton onClick={handleDecrement} size="small" sx={{ padding: '2px' }}>
          <KeyboardArrowDownIcon sx={{ fontSize: '16px' }} />
        </IconButton>
      </div>
    </InputAdornment>
  );

  return (
    <TextField
      name={field.name}
      value={localValue}
      id={id}
      label={label}
      sx={{
        '& .MuiInputBase-root': {
          paddingRight: '8px',
        },
      }}
      slotProps={{
        htmlInput: { min, max, step, suffixText } as React.InputHTMLAttributes<HTMLInputElement>,
        input: {
          inputComponent: InlineSuffixInput,
          ...(showSpinners && { endAdornment: endAdornment() }),
        },
      }}
      onChange={handleChange}
      onBlur={handleBlur}
      error={error}
      helperText={helperText}
      className={className}
      disabled={disabled}
    />
  );
};

export default NumericField;
